import React, { useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  MenuItem,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import PageTitle from "./common/PageTitle";
import GatewayRouteSloSection from "./product-manager/GatewayRouteSloSection";
import {
  DataSensitivity,
  EntityStatus,
  RouteAuthMechanism,
  type API,
  type GatewayRouteSloTarget,
  type Product,
} from "../types";
import { apiService } from "../api/services/apiService";
import { useGatewaySkus } from "../hooks/useGatewaySkus";
import { useApis } from "../hooks/useApis";
import { useProducts } from "../hooks/useProducts";
import { resolveReturnDestination } from "../utils/navigationReturn";
import { PM_HUBS } from "../utils/pmHubRoutes";
import { GATEWAY_ENV_OPTIONS } from "../utils/gatewaySkuEnv";
import { ROUTE_AUTH_OPTIONS, routeAuthLabel } from "../utils/gatewayRouteAuth";
import { DATA_SENSITIVITY_OPTIONS, dataSensitivityLabel } from "../utils/dataSensitivity";

interface AddGatewayRouteProps {
  tenantId: string | null;
  returnPath?: string;
  returnSearch?: string;
}

const AddGatewayRoute: React.FC<AddGatewayRouteProps> = ({
  tenantId,
  returnPath,
  returnSearch,
}) => {
  const navigate = useNavigate();
  const goBack = () =>
    navigate(
      returnPath
        ? resolveReturnDestination(returnPath, returnSearch)
        : PM_HUBS.gatewayRoute,
    );

  const { gatewaySkus } = useGatewaySkus();
  const { apis } = useApis();
  const { products } = useProducts();

  const [basePath, setBasePath] = useState("");
  const [upstreamServer, setUpstreamServer] = useState("");
  const [upstreamPath, setUpstreamPath] = useState("");
  const [envName, setEnvName] = useState("dev");
  const [gatewayId, setGatewayId] = useState("");
  const [gatewaySkuUuid, setGatewaySkuUuid] = useState("");
  const [gatewayAuth, setGatewayAuth] = useState(RouteAuthMechanism.OAUTH2);
  const [upstreamAuth, setUpstreamAuth] = useState(RouteAuthMechanism.BASIC);
  const [gatewayAuthNotes, setGatewayAuthNotes] = useState("");
  const [upstreamAuthNotes, setUpstreamAuthNotes] = useState("");
  const [purpose, setPurpose] = useState("");
  const [dataSensitivity, setDataSensitivity] = useState<DataSensitivity | "">("");
  const [notes, setNotes] = useState("");
  const [sloTargets, setSloTargets] = useState<GatewayRouteSloTarget[]>([]);
  const [selectedApis, setSelectedApis] = useState<API[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
  const [duplicateWarning, setDuplicateWarning] = useState<string | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const effectiveGatewayId =
    gatewaySkuUuid
      ? gatewaySkus.find((s) => s.uuid === gatewaySkuUuid)?.gatewayId ?? gatewayId
      : gatewayId;

  useEffect(() => {
    const check = async () => {
      if (!basePath.trim() || !envName || !effectiveGatewayId.trim()) {
        setDuplicateWarning(null);
        return;
      }
      try {
        const dupes = await apiService.findPotentialDuplicateGatewayRoutes({
          envName,
          gatewayId: effectiveGatewayId.trim(),
          basePath: basePath.trim(),
        });
        if (dupes.length) {
          setDuplicateWarning(
            `A route already exists for ${envName} / ${effectiveGatewayId} / ${basePath} (${dupes[0].name}).`,
          );
        } else {
          setDuplicateWarning(null);
        }
      } catch {
        setDuplicateWarning(null);
      }
    };
    const t = setTimeout(check, 400);
    return () => clearTimeout(t);
  }, [basePath, envName, effectiveGatewayId, gatewaySkuUuid]);

  const canSubmit =
    basePath.trim() &&
    upstreamServer.trim() &&
    upstreamPath.trim() &&
    envName &&
    (gatewaySkuUuid || gatewayId.trim()) &&
    !duplicateWarning;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setErrorMessage(null);
    try {
      const sloPayload = sloTargets
        .filter((s) => s.sloTarget?.trim())
        .map((s) => ({
          ...s,
          sloTarget: s.sloTarget.trim(),
        }));
      await apiService.createGatewayRoute({
        tenantId,
        status: EntityStatus.ACTIVE,
        basePath: basePath.trim(),
        upstreamServer: upstreamServer.trim(),
        upstreamPath: upstreamPath.trim(),
        envName,
        gatewayId: gatewaySkuUuid ? undefined : gatewayId.trim(),
        gatewaySkuUuid: gatewaySkuUuid || undefined,
        gatewayAuthMechanism: gatewayAuth,
        upstreamAuthMechanism: upstreamAuth,
        gatewayAuthNotes: gatewayAuthNotes.trim() || undefined,
        upstreamAuthNotes: upstreamAuthNotes.trim() || undefined,
        purpose: purpose.trim() || undefined,
        dataSensitivity: dataSensitivity || undefined,
        notes: notes.trim() || undefined,
        sloTargets: sloPayload,
        apiUuids: selectedApis.map((a) => a.uuid),
        productUuids: selectedProducts.map((p) => p.uuid),
      });
      setSuccessOpen(true);
      setTimeout(goBack, 800);
    } catch (err: unknown) {
      setErrorMessage(err instanceof Error ? err.message : "Failed to create route");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 960, mx: "auto", p: 2 }}>
      <PageTitle
        pageTitle="Add gateway route"
        pageDescription="Confidential routing documentation"
      />
      {duplicateWarning && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {duplicateWarning}
        </Alert>
      )}
      <Paper sx={{ p: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Routing
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: { md: "1fr 1fr" }, gap: 2 }}>
          <TextField
            label="Gateway base path *"
            value={basePath}
            onChange={(e) => setBasePath(e.target.value)}
            placeholder="/foo"
            fullWidth
          />
          <TextField
            select
            label="Environment *"
            value={envName}
            onChange={(e) => setEnvName(e.target.value)}
            fullWidth
          >
            {GATEWAY_ENV_OPTIONS.map((e) => (
              <MenuItem key={e} value={e}>
                {e}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Gateway SKU (optional)"
            value={gatewaySkuUuid}
            onChange={(e) => setGatewaySkuUuid(e.target.value)}
            fullWidth
          >
            <MenuItem value="">None</MenuItem>
            {gatewaySkus.map((s) => (
              <MenuItem key={s.uuid} value={s.uuid}>
                {s.gatewayCommonName} ({s.envName} / {s.gatewayId})
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Gateway ID *"
            value={gatewayId}
            onChange={(e) => setGatewayId(e.target.value)}
            disabled={!!gatewaySkuUuid}
            fullWidth
          />
          <TextField
            label="Upstream server *"
            value={upstreamServer}
            onChange={(e) => setUpstreamServer(e.target.value)}
            fullWidth
          />
          <TextField
            label="Upstream path *"
            value={upstreamPath}
            onChange={(e) => setUpstreamPath(e.target.value)}
            fullWidth
          />
        </Box>

        <Typography variant="subtitle2" sx={{ mt: 3 }} gutterBottom>
          Authentication
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: { md: "1fr 1fr" }, gap: 2 }}>
          <TextField
            select
            label="Gateway auth *"
            value={gatewayAuth}
            onChange={(e) => setGatewayAuth(e.target.value as RouteAuthMechanism)}
            fullWidth
          >
            {ROUTE_AUTH_OPTIONS.map((a) => (
              <MenuItem key={a} value={a}>
                {routeAuthLabel(a)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Upstream auth *"
            value={upstreamAuth}
            onChange={(e) => setUpstreamAuth(e.target.value as RouteAuthMechanism)}
            fullWidth
          >
            {ROUTE_AUTH_OPTIONS.map((a) => (
              <MenuItem key={a} value={a}>
                {routeAuthLabel(a)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Gateway auth notes"
            value={gatewayAuthNotes}
            onChange={(e) => setGatewayAuthNotes(e.target.value)}
            fullWidth
          />
          <TextField
            label="Upstream auth notes"
            value={upstreamAuthNotes}
            onChange={(e) => setUpstreamAuthNotes(e.target.value)}
            fullWidth
          />
        </Box>

        <TextField
          label="Purpose"
          value={purpose}
          onChange={(e) => setPurpose(e.target.value)}
          fullWidth
          multiline
          minRows={2}
          sx={{ mt: 2 }}
        />
        <TextField
          select
          label="Data sensitivity"
          value={dataSensitivity}
          onChange={(e) => setDataSensitivity(e.target.value as DataSensitivity)}
          fullWidth
          sx={{ mt: 2 }}
        >
          <MenuItem value="">None</MenuItem>
          {DATA_SENSITIVITY_OPTIONS.map((d) => (
            <MenuItem key={d} value={d}>
              {dataSensitivityLabel(d)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          fullWidth
          multiline
          minRows={2}
          sx={{ mt: 2 }}
        />

        <GatewayRouteSloSection rows={sloTargets} onChange={setSloTargets} />

        <Typography variant="subtitle2" sx={{ mt: 3 }} gutterBottom>
          Optional links
        </Typography>
        <Autocomplete
          multiple
          options={apis}
          getOptionLabel={(a) => a.name}
          value={selectedApis}
          onChange={(_, v) => setSelectedApis(v)}
          renderInput={(params) => <TextField {...params} label="APIs" />}
          sx={{ mb: 2 }}
        />
        <Autocomplete
          multiple
          options={products}
          getOptionLabel={(p) => p.name}
          value={selectedProducts}
          onChange={(_, v) => setSelectedProducts(v)}
          renderInput={(params) => <TextField {...params} label="Products" />}
        />

        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting || !canSubmit}>
            {submitting ? "Saving…" : "Create route"}
          </Button>
          <Button onClick={goBack}>Cancel</Button>
        </Box>
      </Paper>

      <Snackbar open={successOpen} message="Gateway route created" />
      <Snackbar open={!!errorMessage} message={errorMessage ?? ""} />
    </Box>
  );
};

export default AddGatewayRoute;
